import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsFilterCriteria, createEmptyCriteria } from './events-filter.model';

@Injectable({ providedIn: 'root' })
export class EventsFilterUrlSyncService {
  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  parseFromQueryParams(params: Record<string, string | undefined>, base: EventsFilterCriteria): EventsFilterCriteria {
    const criteria = { ...base };

    if (params['search']) {
      criteria.searchText = params['search'];
    }

    if (params['type']) {
      criteria.eventType = params['type'].split(',').map((t) => t.trim()).filter(Boolean);
    }

    const orgIds = params['orgIds'] || params['org'];
    if (orgIds) {
      if (/^\d/.test(orgIds)) {
        criteria.organizationIds = orgIds.split(',').map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
      }
    }

    if (params['eps']) {
      criteria.educationalPathIds = params['eps'].split(',').map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
    }

    if (params['pathver']) {
      criteria.educationalPathVersionIds = params['pathver'].split(',').map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
    }

    if (params['mods']) {
      criteria.moduleIds = params['mods'].split(',').map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
    }

    if (params['locs']) {
      criteria.locationGuids = params['locs'].split(',').map((g) => g.trim()).filter(Boolean);
    }

    if (params['hidefull'] === '1' || params['hidefull'] === 'true') {
      criteria.hideFullEvents = true;
    }

    if (params['hidepart'] === '1' || params['hidepart'] === 'true') {
      criteria.hideParticipatedEvents = true;
    }

    if (params['hidesub'] === '0' || params['hidesub'] === 'false') {
      criteria.hideSubEvents = false;
    }

    if (params['showgrp'] === '1' || params['showgrp'] === 'true') {
      criteria.showGroupCalendarEvents = true;
    }

    if (params['lat'] && params['lng']) {
      const lat = parseFloat(params['lat']);
      const lng = parseFloat(params['lng']);
      const radiusKm = params['dist'] ? parseFloat(params['dist']) : null;
      if (!isNaN(lat) && !isNaN(lng)) {
        criteria.distanceFilter = {
          enabled: true,
          latitude: lat,
          longitude: lng,
          radiusKm: radiusKm != null && !isNaN(radiusKm) ? radiusKm : null,
        };
      }
    }

    return criteria;
  }

  hasFilterParams(params: Record<string, string | undefined>): boolean {
    return !!(params['search'] || params['type'] || params['orgIds'] || params['org'] ||
      params['eps'] || params['pathver'] || params['mods'] || params['locs'] ||
      params['hidefull'] || params['hidepart'] || params['showgrp'] || params['lat']);
  }

  syncToUrl(criteria: EventsFilterCriteria): void {
    const queryParams: Record<string, string | null | undefined> = {
      search: criteria.searchText?.trim() || null,
      type: criteria.eventType?.length ? criteria.eventType.join(',') : null,
      orgIds: criteria.organizationIds?.length ? criteria.organizationIds.join(',') : null,
      org: null,
      eps: criteria.educationalPathIds?.length ? criteria.educationalPathIds.join(',') : null,
      pathver: criteria.educationalPathVersionIds?.length ? criteria.educationalPathVersionIds.join(',') : null,
      mods: criteria.moduleIds?.length ? criteria.moduleIds.join(',') : null,
      locs: criteria.locationGuids?.length ? criteria.locationGuids.join(',') : null,
      hidefull: criteria.hideFullEvents ? '1' : null,
      hidepart: criteria.hideParticipatedEvents ? '1' : null,
      hidesub: criteria.hideSubEvents === false ? '0' : null,
      showgrp: criteria.showGroupCalendarEvents ? '1' : null,
      lat: null,
      lng: null,
      dist: null,
    };

    const df = criteria.distanceFilter;
    if (df?.enabled && df.latitude != null && df.longitude != null) {
      queryParams['lat'] = String(df.latitude);
      queryParams['lng'] = String(df.longitude);
      if (df.radiusKm != null && df.radiusKm > 0) {
        queryParams['dist'] = String(df.radiusKm);
      }
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  buildDefaultsFromMetadata(
    metadata: import('./events-filter.model').FilterMetadata
  ): EventsFilterCriteria {
    return {
      ...createEmptyCriteria(),
      eventType: [...(metadata.defaults?.eventTypes || ['education', 'course', 'activity'])],
      organizationIds: [...(metadata.defaultOrganizationIds || [])],
      educationalPathIds: [...(metadata.defaultEducationalPathIds || [])],
      locationGuids: [...(metadata.defaultLocationGuids || [])],
      hideSubEvents: metadata.defaults?.hideSubEvents !== false,
    };
  }

  /**
   * Apply metadata defaults, then overlay only the filter fields present in the URL.
   * Org/path defaults are preserved when only e.g. `mods` is set; module deeplinks
   * use all locations unless `locs` is explicitly in the URL.
   */
  applyDefaultsFromMetadata(
    metadata: import('./events-filter.model').FilterMetadata,
    snapshot: Record<string, string | undefined>
  ): EventsFilterCriteria {
    const criteria = this.parseFromQueryParams(
      snapshot,
      this.buildDefaultsFromMetadata(metadata)
    );
    if (snapshot['mods'] && !Object.prototype.hasOwnProperty.call(snapshot, 'locs')) {
      criteria.locationGuids = [];
    }
    return criteria;
  }
}
